// 批次 19（B8-1，Gitea issue #14）：自製輕量月曆（裁決 R19-2）。
//
// legacy 用 FullCalendar 3.3.1（webjars/fullcalendar/3.3.1；modal/calendar.jsp:31-86 的
// `#calendar` 全月曆＋header prev/next today／title／month,agendaWeek,agendaDay）。
// 批次 14 曾決議 §3.2「不加套件」（B14-1 前例）——本批不自製完全等價的 fullcalendar，
// 以月曆（month view）為準鏡像：月格＋事件底色（backgroundColor）＋today/prev/next＋
// 事件點擊（eventClick：schedule→showScheduleModal；class/meeting→CourseModal，
// calendar.jsp:71-86）。**agendaWeek／agendaDay 兩個檢視不鏡像**（KNOWN-NOT-DONE D19-x）。
//
// 批次 22（B2-2，Gitea issue #7）：課程行事曆（course-clazz-list／course-clazz-query-list）
// 改用本元件。擴充（不影響 dashboard 既有用法）：
//   - 事件型別放寬為結構型泛型（DashboardCalendarEvent 仍相容）。
//   - `initialDate`：fullCalendar `gotoDate(beginDate)` 對應——課程行事曆每次查詢後
//     gotoDate(查詢表單 beginDate)（clazzList_js.jsp:294-295／clazzQueryList.js:1228）。
//   - `onViewChange`：fullCalendar `viewRender` 對應（FC3 初次渲染與換月/今天都會觸發）——
//     月檢視範圍＝當月起訖（ClassUtils.getFullCalendarStartDateAndEndDate month 分支，
//     classUtils.js:340-346）；呼叫端把範圍寫回查詢表單 beginDate/endDate 並重查
//     （clazzList_js.jsp viewRender：2862-2868）。
//   - `renderEvent`：事件格內容自訂（課程查詢頁的行事曆項目＝CourseViewCalendarItem）。
//   - `legend`：eventAfterAllRender 的圖例（「課程 、 會議」，clazzList_js.jsp:2856-2861）。
//
// 【證據等級】§7 第 3 級（原始碼）；視覺為自製近似【雛形值待實機校準】。

import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';

/** 星期表頭（fullcalendar lang:zh-tw；dayNamesShort＝日一二三四五六）。 */
const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'] as const;

/** 事件的最小結構（月曆渲染所需）；呼叫端可帶更寬的型別（泛型 E 保留原欄位）。 */
export interface LightCalendarEventBase {
  readonly id: string;
  readonly title: string;
  /** `YYYY-MM-DD HH:mm`（slice(0,10)=日鍵、slice(11,16)=H:mm 前綴）。 */
  readonly start: string;
  readonly backgroundColor: string;
}

export interface LightCalendarProps<E extends LightCalendarEventBase = LightCalendarEventBase> {
  /** `data-ui-id` 前綴（＝`${pageId}-calendar`）。 */
  readonly uiId: string;
  readonly events: readonly E[];
  /** 事件點擊（fullCalendar eventClick 對應）；未提供＝事件塊不可點。 */
  readonly onEventClick?: ((event: E) => void) | undefined;
  /** gotoDate(beginDate) 對應：外部指定的初始月（`YYYY-MM-DD`）；未提供＝真實今天。 */
  readonly initialDate?: string | undefined;
  /** viewRender 對應：月檢視起訖（初次渲染＋換月/今天都會呼叫，fullCalendar FC3 行為）。 */
  readonly onViewChange?: ((beginDate: string, endDate: string) => void) | undefined;
  /** 事件格內容自訂（未提供＝預設 `H:mm title`）。 */
  readonly renderEvent?: ((event: E) => ReactNode) | undefined;
  /** 工具列圖例（eventAfterAllRender 對應；課程行事曆＝「課程 、 會議」）。 */
  readonly legend?: ReactNode | undefined;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** month 檢視的起訖（classUtils.js:342-346：startOf month ~ endOf month）。 */
function monthBounds(year: number, month: number): [string, string] {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  return [`${year}-${pad2(month + 1)}-01`, `${year}-${pad2(month + 1)}-${pad2(daysInMonth)}`];
}

/** `YYYY-MM-DD` → 年月（解析失敗＝null）。 */
function parseYearMonth(value: string | undefined): { year: number; month: number } | null {
  if (value === undefined) return null;
  const match = /^(\d{4})-(\d{2})/.exec(value);
  if (match === null) return null;
  return { year: Number(match[1]), month: Number(match[2]) - 1 };
}

/**
 * 月曆。佈局鏡像 fullcalendar month view：上列控制（prev/next/today＋`YYYY年M月`）＋
 * 7 欄星期列＋ 5~6 週月格。事件以 `H:mm title` 呈現、底色＝backgroundColor
 * （modal/calendar.jsp 的 events 由 getCalendarEventsAjax 種子帶入）。
 */
export function LightCalendar<E extends LightCalendarEventBase = LightCalendarEventBase>({
  uiId,
  events,
  onEventClick,
  initialDate,
  onViewChange,
  renderEvent,
  legend,
}: LightCalendarProps<E>): ReactElement {
  const now = new Date();
  const [cursor, setCursor] = useState(() => {
    const initial = parseYearMonth(initialDate);
    return initial ?? { year: now.getFullYear(), month: now.getMonth() };
  });
  const today = new Date();

  // gotoDate(beginDate) 對應：initialDate 的月份變了就跟著移動（同月內改日期不重置）。
  const initialMonthKey = initialDate === undefined ? '' : (initialDate.slice(0, 7) ?? '');
  useEffect(() => {
    if (initialMonthKey === '') return;
    const initial = parseYearMonth(`${initialMonthKey}-01`);
    if (initial === null) return;
    setCursor((current) =>
      current.year === initial.year && current.month === initial.month
        ? current
        : { year: initial.year, month: initial.month },
    );
  }, [initialMonthKey]);

  // viewRender 對應：初次渲染＋每次月游標變動都回報月檢視起訖（呼叫端寫回查詢表單＋重查）。
  useEffect(() => {
    if (onViewChange === undefined) return;
    const [beginDate, endDate] = monthBounds(cursor.year, cursor.month);
    onViewChange(beginDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 只跟月游標走；callback 由呼叫端保持穩定
  }, [cursor]);

  const cells = useMemo(() => {
    const first = new Date(cursor.year, cursor.month, 1);
    const startOffset = first.getDay(); // 週日=0
    const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
    const result: { date: Date | null }[] = [];
    for (let i = 0; i < startOffset; i += 1) result.push({ date: null });
    for (let day = 1; day <= daysInMonth; day += 1) {
      result.push({ date: new Date(cursor.year, cursor.month, day) });
    }
    while (result.length % 7 !== 0) result.push({ date: null });
    return result;
  }, [cursor]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, E[]>();
    for (const event of events) {
      const key = event.start.slice(0, 10);
      const list = map.get(key) ?? [];
      list.push(event);
      map.set(key, list);
    }
    return map;
  }, [events]);

  function shiftMonth(delta: number): void {
    setCursor((current) => {
      const next = new Date(current.year, current.month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });
  }

  function goToday(): void {
    setCursor({ year: today.getFullYear(), month: today.getMonth() });
  }

  return (
    <div data-ui-id={uiId} className="min-w-[560px]">
      <div data-ui-id={`${uiId}-toolbar`} className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button type="button" data-ui-id={`${uiId}-prev`} onClick={() => shiftMonth(-1)}>
            ‹
          </button>
          <button type="button" data-ui-id={`${uiId}-today`} onClick={goToday}>
            今天
          </button>
          <button type="button" data-ui-id={`${uiId}-next`} onClick={() => shiftMonth(1)}>
            ›
          </button>
          {legend === undefined ? null : <span className="ml-2">{legend}</span>}
        </div>
        <span data-ui-id={`${uiId}-title`} className="text-[18px] font-bold">
          {cursor.year}年{cursor.month + 1}月
        </span>
        <span data-ui-id={`${uiId}-view`} className="text-[13px] text-[#999]">
          月
        </span>
      </div>
      <div data-ui-id={`${uiId}-weekdays`} className="grid grid-cols-7 border border-border text-center">
        {WEEKDAYS.map((weekday, index) => (
          <div key={weekday} data-ui-id={`${uiId}-weekday-${index}`} className="bg-[#f5f5f5] p-1 text-[13px] font-bold">
            {weekday}
          </div>
        ))}
      </div>
      <div data-ui-id={`${uiId}-grid`} className="grid grid-cols-7 border-x border-b border-border">
        {cells.map((cell, index) => {
          const date = cell.date;
          const key = date
            ? `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
            : `empty-${index}`;
          const dayEvents = date ? (eventsByDay.get(key) ?? []) : [];
          const isToday =
            date !== null &&
            date.getFullYear() === today.getFullYear() &&
            date.getMonth() === today.getMonth() &&
            date.getDate() === today.getDate();
          return (
            <div
              key={key}
              data-ui-id={`${uiId}-cell-${key}`}
              className={`min-h-[72px] border-t border-l border-border p-1 ${date === null ? 'bg-[#fafafa]' : ''} ${isToday ? 'bg-[#fcf8e3]' : ''}`}
            >
              {date === null ? null : (
                <span data-ui-id={`${uiId}-cell-${key}-day`} className="text-[12px]">
                  {date.getDate()}
                </span>
              )}
              {dayEvents.map((event) => (
                <div
                  key={event.id}
                  data-ui-id={`${uiId}-event-${event.id}`}
                  data-event-type={'eventType' in event ? String((event as { eventType?: unknown }).eventType ?? '') : ''}
                  className="mb-[2px] rounded px-1 text-[11px] text-white"
                  style={{ backgroundColor: event.backgroundColor }}
                  onClick={onEventClick === undefined ? undefined : () => onEventClick(event)}
                  title={`${event.start.slice(11, 16)} ${event.title}`}
                >
                  {renderEvent === undefined ? (
                    <span className="block cursor-pointer truncate">
                      {event.start.slice(11, 16)} {event.title}
                    </span>
                  ) : (
                    renderEvent(event)
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
